import React from 'react';
import { 
  Container, Paper, Box, Typography, Button, IconButton, 
  Snackbar, Alert, CircularProgress 
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import ShareIcon from '@mui/icons-material/Share';
import { ThemeProvider } from '@mui/material/styles';

import theme from '../../../theme/theme';
import { DRAFT_KEYS } from '../../../utils/draftUtils';
import DraftSaveButton from '../../../components/DraftSaveButton';

import { useAldreteScore } from './hooks/useAldreteScore';
import PatientInfo from './screens/PatientInfo';
import AldreteTable from './screens/AldreteTable';

const AldreteScore = () => {
  const { uhid_no } = useParams();
  const navigate = useNavigate();

  const {
    formData, setFormData,
    errors, setErrors,
    success,
    loading,
    submitStatus, setSubmitStatus,
    handleInputChange, handleCheckboxClick, handleScoreChange, 
    handleBaseTimeChange, handleAdmTimeChange, handleDischargeTimeChange, handleBlur,
    handleSubmit, handleShareClick,
    mutationLoading
  } = useAldreteScore(uhid_no, navigate);

  const handleBack = () => {
    navigate(`/mainPage/${uhid_no}`, {
      state: { from: 'PostOperativePhase', status: 'success', step: 3 },
    });
  };

  const handleHomeNavigation = () => {
    navigate('/home');
  };

  const goToVideoRecorder = (formName, uhid) => {
    navigate(`/video?form=${encodeURIComponent(formName)}&uhid_no=${encodeURIComponent(uhid)}`);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <Container maxWidth="lg" sx={{ marginTop: 4, marginBottom: 4 }}>
        <Paper elevation={3} sx={{ padding: 3 }}>
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 2, mb: 2 }}>
            <Typography
              variant="h4"
              sx={{
                color: '#008080',
                display: 'flex',
                alignItems: 'center',
                fontWeight: 600,
              }}
            >
              ALDRETE SCORE
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <img src="/logo.png" alt="Logo" className="w-8 h-8" />
              <Typography
                variant="h6"
                sx={{
                  color: '#008080',
                  marginLeft: 1,
                  fontWeight: 500,
                }}
              >
                OTMS
              </Typography>
            </Box>
          </Box>

          {/* Navigation Controls */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3 }}>
            <Button
              variant="outlined"
              onClick={handleBack}
              sx={{ marginLeft: 2 }}
            >
              Back
            </Button>

            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Button
                variant="contained"
                onClick={handleHomeNavigation}
                sx={{ marginRight: 1 }}
              >
                Home
              </Button>
              <Button
                variant="contained"
                onClick={() => goToVideoRecorder("ALDRETE SCORE", uhid_no)}
                sx={{ marginRight: 1 }}
              >
                Add Iterative Video
              </Button>
              <IconButton color="primary" aria-label="share" onClick={handleShareClick}>
                <ShareIcon />
              </IconButton>
            </Box>
          </Box>

          <form onSubmit={handleSubmit}>
            <Box sx={{ px: 2, mb: 4 }}>
              <PatientInfo 
                formData={formData}
                setFormData={setFormData}
                errors={errors}
                setErrors={setErrors}
                handleInputChange={handleInputChange}
                handleBlur={handleBlur}
              />
            </Box>

            <Box sx={{ px: 2, mb: 4 }}>
              <AldreteTable 
                formData={formData}
                handleScoreChange={handleScoreChange}
                handleBaseTimeChange={handleBaseTimeChange}
                handleAdmTimeChange={handleAdmTimeChange}
                handleDischargeTimeChange={handleDischargeTimeChange}
                handleCheckboxClick={handleCheckboxClick}
              />
            </Box>

            {/* Form Actions */}
            <Box sx={{ display: 'flex', gap: 2, px: 2, marginTop: 4 }}>
              <DraftSaveButton
                formKey={DRAFT_KEYS.POST_OPERATIVE_ANALGESIA_ASSESSMENT}
                uhidNo={uhid_no}
                formData={formData}
                onDraftLoaded={(draftData) => setFormData(prev => ({ ...prev, ...draftData }))}
                variant="outlined"
                size="large"
                isUpdate={success}
              />
              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                size="large"
                disabled={mutationLoading}
                startIcon={mutationLoading ? <CircularProgress size={20} color="inherit" /> : null}
              >
                {mutationLoading ? 'Submitting...' : (success ? 'Update Score' : 'Submit Score')}
              </Button>
            </Box>
          </form>

          {/* Feedback SnackBar */}
          <Snackbar 
            open={submitStatus.open} 
            autoHideDuration={6000} 
            onClose={() => setSubmitStatus(prev => ({ ...prev, open: false }))}
          >
            <Alert 
              onClose={() => setSubmitStatus(prev => ({ ...prev, open: false }))} 
              severity={submitStatus.severity} 
              sx={{ width: '100%' }}
            >
              {submitStatus.message}
            </Alert>
          </Snackbar>

          {success && (
            <Alert severity="info" sx={{ mt: 2, mx: 2 }}>
              You are viewing/updating an existing record for this patient.
            </Alert>
          )}
        </Paper>
      </Container>
    </ThemeProvider>
  );
};

export default AldreteScore;
